import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Wheat, LogOut, BarChart3, ListChecks, Lock, Upload, ShieldAlert, Activity, Tag } from "lucide-react";
import { enableGranosPWA } from "./pwa";
import GranosLogin from "./GranosLogin";
import GranosResumen from "./GranosResumen";
import GranosContratos from "./GranosContratos";
import GranosPosicion from "./GranosPosicion";
import GranosPrecioDeseado from "./GranosPrecioDeseado";
import GranosCierre from "./GranosCierre";
import GranosImportar from "./GranosImportar";

const CIERRE_USERS = ["bruno.martins@tecnomyl.com", "ingrid.bayer@tecnomyl.com"];

export default function GranosApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("resumen");

  useEffect(() => { enableGranosPWA(); }, []);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f5233]">
        <Loader2 className="h-8 w-8 animate-spin text-lime-300" />
      </div>
    );
  }
  if (!user) return <GranosLogin />;

  const email = (user.email || "").toLowerCase();
  const role = (user.role || "").toLowerCase();
  const hasAccess = user.granos_access === true;

  if (!hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-8 text-center" data-testid="granos-no-access">
        <ShieldAlert className="h-12 w-12 text-red-500" />
        <h1 className="mt-4 text-lg font-bold text-slate-800">Acceso restringido</h1>
        <p className="mt-1 text-sm text-slate-500">Esta app es exclusiva del equipo de Granos.</p>
        <Button onClick={logout} variant="outline" className="mt-6 gap-2"><LogOut className="h-4 w-4" /> Salir</Button>
      </div>
    );
  }

  const isCierre = CIERRE_USERS.includes(email) || role === "admin";
  const canAuthorize = email === "bruno.martins@tecnomyl.com" || role === "admin";

  const tabs = [
    { k: "resumen", label: "Resumen", icon: BarChart3 },
    { k: "posicion", label: "Posición", icon: Activity },
    { k: "precio", label: "Precio", icon: Tag },
    { k: "contratos", label: "Contratos", icon: ListChecks },
    ...(isCierre ? [{ k: "cierre", label: "Cerrar", icon: Lock }] : []),
    { k: "importar", label: "Importar", icon: Upload },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="granos-app">
      <header className="bg-[#0f5233] text-white sticky top-0 z-20" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center">
            <Wheat className="h-5 w-5 text-lime-300" />
          </div>
          <div className="leading-tight">
            <p className="font-bold text-[15px]">Tecnomyl Granos</p>
            <p className="text-[11px] text-emerald-100/70 truncate max-w-[180px]">{user.name || user.email}</p>
          </div>
          <button onClick={logout} data-testid="granos-logout" className="ml-auto rounded-lg bg-white/10 p-2 active:bg-white/20">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 pb-24">
        {tab === "resumen" && <GranosResumen />}
        {tab === "posicion" && <GranosPosicion setTab={setTab} />}
        {tab === "precio" && <GranosPrecioDeseado />}
        {tab === "contratos" && <GranosContratos />}
        {tab === "cierre" && isCierre && <GranosCierre user={user} canAuthorize={canAuthorize} />}
        {tab === "importar" && <GranosImportar onDone={() => setTab("resumen")} />}
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-20 bg-white border-t border-slate-200"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="granos-bottom-nav">
        <div className="grid" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
          {tabs.map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`granos-tab-${k}`}
              className={`flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                tab === k ? "text-[#0f5233]" : "text-slate-400"
              }`}>
              <Icon className={`h-5 w-5 ${tab === k ? "text-[#0f5233]" : "text-slate-400"}`} />
              {label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
